import * as React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import {
  Button,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
} from '@chakra-ui/react';
import { useAppDispatch } from 'hooks';
import { AsyncThunkAction, unwrapResult } from '@reduxjs/toolkit';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Button {
  children?: React.ReactNode;
  [key: string]: any;
}

interface ActionButtonProps extends Button {
  action?: AsyncThunkAction<any, any, any>;
  mutation?: (values?: any) => void | Promise<unknown>;
  redirectOnSuccess?: boolean | string;
  onModalClose?: () => void;
  removeAction?: boolean;
  alertHeader?: string;
  alertBody?: string | React.ReactNode;
  alertSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  alertButtonConfirmationText?: string;
}

export const SubmitButton = ({ children, ...props }: Button): JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { isSubmitting } = useFormState({ control });
  return (
    <Button isLoading={isSubmitting} variant='primary' type='submit' {...props}>
      {children || t('form.saveChanges', 'Zapisz zmiany')}
    </Button>
  );
};

export const ResetButton = ({ children, ...props }: Button): JSX.Element => {
  const { t } = useTranslation();
  const { control, reset } = useFormContext();
  const { isSubmitting, isDirty } = useFormState({ control });
  const [disabled, setDisabled] = React.useState(true);
  function handleClick(): void {
    reset();
  }
  React.useEffect(() => {
    if (isDirty) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [isDirty]);
  return (
    <Button
      isLoading={isSubmitting}
      onClick={handleClick}
      isDisabled={disabled}
      {...props}
      variant='secondary'
    >
      {children || t('form.resetChanges', 'Zresetuj zmiany')}
    </Button>
  );
};

export const ClearDraftButton = ({
  children,
  defaultValuesSchema,
  isDraftAvailable = false,
  preserveFields = [],
  onDraftDiscarded,
  ...props
}: Button & {
  defaultValuesSchema?: Record<string, any>;
  isDraftAvailable?: boolean;
  preserveFields?: string[];
  onDraftDiscarded?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const { control, reset, getValues } = useFormContext();
  const { isSubmitting } = useFormState({ control });

  /**
   * handleClick()
   *
   * preserveFields:
   * use case is: reset form not products, to do so, we have to keep products in RHF
   * to do so we reset it but preserve products.
   */
  function handleClick(): void {
    const preserved = Object.fromEntries(
      preserveFields.map(field => [field, getValues(field)]),
    );
    reset({ ...defaultValuesSchema, ...preserved });
    if (onDraftDiscarded) {
      onDraftDiscarded();
    }
  }

  return (
    <Button
      isLoading={isSubmitting}
      onClick={handleClick}
      isDisabled={!isDraftAvailable}
      {...props}
      variant='secondary'
    >
      {children || t('form.resetChanges', 'Zresetuj zmiany')}
    </Button>
  );
};

export const ActionButton = ({
  children,
  action,
  redirectOnSuccess,
  onModalClose,
  removeAction = false,
  ...props
}: ActionButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast({
    position: 'bottom',
    duration: 5000,
    isClosable: true,
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const history = useHistory();
  const mountedRef = React.useRef<boolean>(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = async () => {
    if (removeAction) {
      if (onModalClose && mountedRef.current) {
        onModalClose();
      }
      if (redirectOnSuccess && mountedRef.current) {
        history.goBack();
      }
      return;
    }

    try {
      if (mountedRef.current) setIsSubmitting(true);
      const actionResponse = await dispatch(action);
      unwrapResult(actionResponse);

      if (onModalClose) onModalClose();

      toast({
        title: t('actionSuccessful', 'Akcja zakończona sukcesem'),
        status: 'success',
      });

      if (redirectOnSuccess && mountedRef.current) {
        if (typeof redirectOnSuccess === 'string') {
          history.push(redirectOnSuccess);
        } else {
          history.goBack();
        }
      }
    } catch (error) {
      if (mountedRef.current) {
        toast({
          title: t('errorOccurred', 'Wystąpił błąd'),
          status: 'error',
        });
      }
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  return (
    <Button isLoading={isSubmitting} onClick={handleClick} {...props}>
      {children}
    </Button>
  );
};

export const ToastButton = ({ children, ...props }: Button): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast({
    position: 'bottom',
    duration: 5000,
    isClosable: true,
  });

  const [isDisabled, setIsDisabled] = React.useState(false);
  const mountedRef = React.useRef<boolean>(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = () => {
    if (mountedRef.current) {
      toast({
        title: t('orderGenerated', 'Zlecenie zostało wygenerowane'),
        status: 'success',
      });
      setIsDisabled(true);
    }
  };

  return (
    <Button onClick={handleClick} disabled={isDisabled} {...props}>
      {children}
    </Button>
  );
};

export const ActionButtonWithConfirmation = ({
  children,
  action,
  mutation,
  redirectOnSuccess,
  alertHeader,
  alertBody,
  alertSize = 'md',
  alertButtonConfirmationText,
  ...props
}: ActionButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast({
    position: 'bottom',
    duration: 5000,
    isClosable: true,
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const history = useHistory();

  const [isAlertOpen, setIsAlertOpen] = React.useState(false);
  const onClose = () => setIsAlertOpen(false);

  const actionRef = React.useRef<HTMLButtonElement>(null);
  const mountedComponentRef = React.useRef<boolean>(true);

  const handleClick = () => setIsAlertOpen(true);

  const handleAction = async () => {
    try {
      if (mountedComponentRef.current) setIsSubmitting(true);
      if (mutation) {
        await mutation();
      } else {
        const actionResponse = await dispatch(action);
        unwrapResult(actionResponse);
      }
      if (mountedComponentRef.current) setIsSubmitting(false);
      if (mountedComponentRef.current) {
        toast({
          title: t('actionSuccessful', 'Akcja zakończona sukcesem'),
          status: 'success',
        });
      }

      if (redirectOnSuccess && mountedComponentRef.current) {
        if (typeof redirectOnSuccess === 'string') {
          history.push(redirectOnSuccess);
        } else {
          history.goBack();
        }
      }
      if (mountedComponentRef.current) onClose();
    } catch (error) {
      if (mountedComponentRef.current) {
        setIsSubmitting(false);
        toast({
          title: t('errorOccurred', 'Wystąpił błąd'),
          status: 'error',
        });
      }
    }
  };

  React.useEffect(() => {
    mountedComponentRef.current = true;
    return () => {
      mountedComponentRef.current = false;
    };
  }, []);

  return (
    <>
      <Button isLoading={isSubmitting} onClick={handleClick} {...props}>
        {children}
      </Button>

      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={actionRef}
        onClose={onClose}
        closeOnOverlayClick={!isSubmitting}
        closeOnEsc={!isSubmitting}
        size={alertSize}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='xl' fontWeight='bold'>
              {alertHeader || children}
            </AlertDialogHeader>

            <AlertDialogBody>
              {alertBody || t('areYouSure', 'Czy jesteś pewien?')}
            </AlertDialogBody>

            <AlertDialogFooter justifyContent='space-between'>
              <Button
                ref={actionRef}
                onClick={onClose}
                isDisabled={isSubmitting}
                minWidth='12ch'
                variant='cancel'
              >
                {t('cancel', 'Anuluj')}
              </Button>
              <Button
                onClick={handleAction}
                ml={3}
                isLoading={isSubmitting}
                minWidth='12ch'
                {...props}
                size='md'
              >
                {alertButtonConfirmationText || children}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

interface ButtonWithErrorProps {
  children?: React.ReactNode;
  errorPath?: string;
  [key: string]: any;
}

export const ButtonWithError = ({
  children,
  errorPath,
  ...props
}: ButtonWithErrorProps): JSX.Element => {
  const { formState } = useFormContext();
  const errorValue = errorPath
    ? errorPath
        ?.split('.')
        .reduce((acc, key) => (acc as any)?.[key], formState.errors)
    : undefined;

  const hasError =
    typeof (errorValue as any)?.message === 'string' ||
    typeof errorValue === 'string';

  return (
    <Box
      borderWidth={hasError ? '2px' : '0'}
      borderColor={hasError ? 'red.500' : 'transparent'}
      p={hasError ? '1' : '0'}
    >
      <Button {...props}>{children}</Button>
    </Box>
  );
};
